import React, { useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import PaymentForm from "./PaymentForm";
import { loadStripe } from "@stripe/stripe-js";
import { IPaymentState } from "interfaces/PaymentInterface";
import { STRIPE_KEY } from "services/endpoint-urls";
import FormikInput from "components/input/formik-input";
import Button from "components/button/button";
import { Form, Formik } from "formik";
import { displayError, displaySuccess } from "components/Toast/toast.component";
import { useMutation } from "@tanstack/react-query";
import { validatePromoCode } from "services/promo.service";
import { IPromoValidateDTO, IPromoValidateResponse } from "interfaces/BaseInterface";
const stripePromise = loadStripe(STRIPE_KEY);

type InitialValue = {
  code: string
}

const initialValues: InitialValue = {
  code: "",
};

const PaymentScreen: React.FC<IPaymentState> = ({
  onGoback,
  clientSecret,
  onSuccess,
  priceInfo,
  email,
  isGift,
  recipientEmails,
}) => {
  const [fullyDiscounted, setFullyDiscounted] = useState(false);

  const { mutate: validatePromoCodes, isPending: isPromoValidating, data: validatedReponse } = useMutation<IPromoValidateResponse, Error, IPromoValidateDTO>({
    mutationFn: (data: IPromoValidateDTO) => validatePromoCode(data),
    retry: 0,
    onSuccess: (data) => {
      if (data.payment_required === false) {
        setFullyDiscounted(true);
        displaySuccess("Your subscription has been activated with a 100% discount!");
      } else {
        displaySuccess("Promo Code Validated");
      }
    }
  });

  const submitHandler = async (values: InitialValue) => {
    try {
      if (clientSecret) { validatePromoCodes({ coupon: values.code, client_secret: clientSecret }) }
    } catch (error) {
      displayError("Unable to Verify");
    }
  };

  return (
    <div className="flex flex-col flex-1 w-full bg-brand">
      <div className="flex flex-col items-center flex-1 px-4 py-12">
      <div className="flex flex-col items-center gap-10 w-full max-w-[850px]">
        <header className="flex flex-col items-center gap-4">
          <h1 className="text-display-lg text-white text-center">
            {fullyDiscounted ? "Payment Complete" : "Complete Your Purchase"}
          </h1>
          <p className="text-base font-normal leading-4 text-white text-center">
            {fullyDiscounted ? "Your subscription is now active." : "Enter your billing details below"}
          </p>
        </header>

        <div className="flex flex-col md:flex-row gap-6 w-full">
          {/* Left — Billing */}
          <div className="bg-white rounded-lg shadow-card flex-1 p-8 order-2 md:order-1">
            {fullyDiscounted ? (
              <div className="flex flex-col items-center gap-4 py-8">
                <span className="text-action text-h2 text-center">
                  Your subscription has been activated with a 100% discount!
                </span>
                <Button
                  type="button"
                  variant="primary"
                  onClick={onSuccess}
                  className="w-full mt-4"
                >
                  Continue
                </Button>
              </div>
            ) : (
              clientSecret && stripePromise && (
                <div>
                  <h2 className="text-h2 text-text-heading mb-6">Billing Information</h2>
                  <Elements stripe={stripePromise} options={{
                    clientSecret,
                    appearance: {
                      theme: 'stripe',
                      variables: {
                        fontFamily: '"Nunito", sans-serif',
                        fontSizeBase: '14px',
                        colorPrimary: '#213269',
                        colorBackground: '#FFFFFF',
                        colorText: '#3A3A3A',
                        colorTextPlaceholder: '#A1A1AA',
                        colorDanger: '#DB5C5C',
                        borderRadius: '6px',
                        spacingUnit: '4px',
                      },
                      rules: {
                        '.Input': {
                          border: '1px solid #E8E8E8',
                          boxShadow: 'none',
                          fontSize: '14px',
                          padding: '10px 12px',
                        },
                        '.Input:focus': {
                          border: '1px solid #213269',
                          boxShadow: '0 0 0 1px #213269',
                        },
                        '.Label': {
                          fontWeight: '600',
                          fontSize: '12px',
                          color: '#3A3A3A',
                        },
                        '.Tab': {
                          border: '1px solid #E8E8E8',
                          boxShadow: 'none',
                        },
                        '.Tab--selected': {
                          border: '1px solid #213269',
                          color: '#213269',
                        },
                        '.Tab:hover': {
                          border: '1px solid #213269',
                        },
                      },
                    },
                  }}>
                    <PaymentForm
                      onGoback={onGoback}
                      onSuccess={onSuccess}
                      email={email}
                      isGift={isGift}
                      recipientEmails={recipientEmails}
                    />
                  </Elements>
                </div>
              )
            )}
          </div>

          {/* Right — Order Summary */}
          <div className="bg-white rounded-lg shadow-card w-full md:w-[300px] shrink-0 p-6 order-1 md:order-2">
            {priceInfo && (
              <div className="flex flex-col gap-4">
                <h3 className="text-h2 text-text-heading">Order Summary</h3>

                <div className="flex justify-between pb-3 border-b border-border">
                  <span className="text-body text-text-heading">Glimpse Membership</span>
                  <span className="text-body text-text-heading">$ {priceInfo.amount.toFixed(2)}</span>
                </div>

                <div className="flex flex-col gap-2 pb-3 border-b border-border">
                  <div className="flex justify-between">
                    <span className="text-body text-text-muted">Subtotal</span>
                    <span className="text-body text-text-heading">$ {priceInfo.amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-body text-text-muted">Tax</span>
                    <span className="text-body text-text-heading">$ {priceInfo.tax.toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex justify-between">
                  <span className="text-body-emphasis text-text-heading">Order Total</span>
                  <span className="text-body-emphasis text-text-heading">
                    $ {(priceInfo.amount + priceInfo.tax).toFixed(2)}
                  </span>
                </div>

                {validatedReponse && (
                  <div className="flex flex-col gap-3">
                    <div className="flex justify-between pb-3 border-b border-border">
                      <span className="text-body-emphasis text-action">Promo Discount</span>
                      <span className="text-body-emphasis text-action">
                        - $ {validatedReponse?.discount?.discount_amount.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-body-emphasis text-text-heading">Total After Discount</span>
                      <span className="text-body-emphasis text-text-heading">
                        $ {validatedReponse?.amount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                {!fullyDiscounted && (
                  <Formik initialValues={initialValues} onSubmit={submitHandler}>
                    {() => (
                      <Form autoComplete="off" className="flex flex-col gap-3 pt-2 border-t border-border">
                        <FormikInput
                          type="text"
                          id="code"
                          label="Promo Code"
                          name="code"
                          placeholder="Add Promo Code"
                          maxLength={50}
                          sanitized={true}
                          disabled={!!validatedReponse}
                        />
                        <Button
                          type="submit"
                          variant="primary"
                          disabled={isPromoValidating || !!validatedReponse}
                          className="w-full"
                        >
                          {isPromoValidating ? "Loading..." : "Apply"}
                        </Button>
                      </Form>
                    )}
                  </Formik>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default PaymentScreen;
